// Frontend RBAC catalog — the SINGLE SOURCE mirror of the rebuilt
// backend `PERMISSIONS` catalog (`backend/src/platform/auth/permissions.ts`).
// Every string here MUST be a key the backend actually enforces; a
// frontend permission the server never checks is a DEAD gate that only
// bounces authorized users to /unauthorized. Reconciled against the
// 25-key backend catalog in #943.
//
// Notable folds from the archived backend catalog:
//   - No `:list` action exists any more — list + detail are gated under
//     one `<module>:read` key.
//   - kitchen/supply granular keys (ingredients/dishes/combos/suppliers/
//     supplier_pricing/menu_plans) folded into `catalog:read` / `catalog:write`.
//   - inventory / stocktake / procurement each have their own module keys.
//   - drivers/tasks folded into `dispatch:*`; exceptions/monitoring/
//     infra:failed_events folded into `webhook_failures:*`.
//
// The caller's effective grants come from `GET /api/permissions`
// (`data.data.granted`), fetched in BetterAuthContext.
export const PERMISSIONS = {
  USERS_READ: 'users:read',
  USERS_WRITE: 'users:write',
  PRODUCTS_READ: 'products:read',
  PRODUCTS_WRITE: 'products:write',
  ORDERS_READ: 'orders:read',
  CUSTOMERS_READ: 'customers:read',
  /** One-off backfill jobs. Hidden sub-grant (not in the grid). */
  SYSTEM_BACKFILL: 'system:backfill',
  DISPATCH_READ: 'dispatch:read',
  DISPATCH_WRITE: 'dispatch:write',
  PACKAGING_READ: 'packaging:read',
  /** Gates the Export buttons on PackagingList. Hidden sub-grant
   *  (not in the grid). */
  PACKAGING_EXPORT: 'packaging:export',
  REVIEW_READ: 'review:read',
  /** Resolve an operator-review-queue row. Hidden sub-grant (not in the grid). */
  REVIEW_RESOLVE: 'review:resolve',
  WEBHOOK_FAILURES_READ: 'webhook_failures:read',
  WEBHOOK_FAILURES_WRITE: 'webhook_failures:write',
  CATALOG_READ: 'catalog:read',
  CATALOG_WRITE: 'catalog:write',
  INVENTORY_READ: 'inventory:read',
  INVENTORY_WRITE: 'inventory:write',
  STOCKTAKE_READ: 'stocktake:read',
  STOCKTAKE_WRITE: 'stocktake:write',
  PROCUREMENT_READ: 'procurement:read',
  PROCUREMENT_WRITE: 'procurement:write',
  /** The resource-change ledger behind the change-history tab (#1964). */
  AUDIT_READ: 'audit:read',
  /** The per-order trace — raw webhook bodies, so the tightest read (#1964). */
  DEBUG_READ: 'debug:read',
  ANALYTICS_READ: 'analytics:read',
  ANALYTICS_GENERATE: 'analytics:generate',
} as const;

/**
 * Any key in the catalogue. Declared here, next to the catalogue itself, so the
 * action layer and the sidebar check against one union rather than each growing
 * a private copy.
 */
export type PermissionKey = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export const ALL_PERMISSIONS = Object.values(PERMISSIONS);

// The Users admin grid renders one column per ACTIONS entry and derives
// the permission string as `${resource}:${action}`. Every (resource,
// action) pair below therefore MUST resolve to a real backend key.
// Non-CRUD singletons (`packaging:export`, `review:resolve`,
// `system:backfill`) are hidden sub-grants — NOT grid actions — assigned
// per-seat instead.
export const PERMISSION_GROUPS = [
  { resource: 'users', label: 'perm.users', actions: ['read', 'write'] as const },
  { resource: 'products', label: 'perm.products', actions: ['read', 'write'] as const },
  { resource: 'orders', label: 'perm.orders', actions: ['read'] as const },
  { resource: 'customers', label: 'perm.customers', actions: ['read'] as const },
  { resource: 'dispatch', label: 'perm.dispatch', actions: ['read', 'write'] as const },
  { resource: 'packaging', label: 'perm.packaging', actions: ['read'] as const },
  { resource: 'review', label: 'perm.review', actions: ['read'] as const },
  {
    resource: 'webhook_failures',
    label: 'perm.webhook_failures',
    actions: ['read', 'write'] as const,
  },
  { resource: 'catalog', label: 'perm.catalog', actions: ['read', 'write'] as const },
  { resource: 'inventory', label: 'perm.inventory', actions: ['read', 'write'] as const },
  { resource: 'stocktake', label: 'perm.stocktake', actions: ['read', 'write'] as const },
  { resource: 'procurement', label: 'perm.procurement', actions: ['read', 'write'] as const },
  // Diagnostic reads (#1964). In the grid because an admin has to be able to
  // hand them out — a key excluded from every role preset and absent from the
  // grid could only be granted by editing the database.
  { resource: 'audit', label: 'perm.audit', actions: ['read'] as const },
  { resource: 'debug', label: 'perm.debug', actions: ['read'] as const },
] as const;

export const ACTIONS = ['read', 'write'] as const;

export const ROLE_LABELS: Record<string, string> = {
  admin: 'role.admin',
  operator: 'role.operator',
  viewer: 'role.viewer',
};

export const ROLES = ['admin', 'operator', 'viewer'] as const;

export const ROLE_DESCRIPTIONS: Record<string, string> = {
  admin: 'role.admin.desc',
  operator: 'role.operator.desc',
  viewer: 'role.viewer.desc',
};

// Keys no non-admin seat receives by default — mirrors the backend's
// `NON_ADMIN_EXCLUDED`: user management is admin-only, and the one-off
// historical backfill is a data-rewriting job, not daily operations.
const NON_ADMIN_EXCLUDED: readonly string[] = [
  PERMISSIONS.USERS_READ,
  PERMISSIONS.USERS_WRITE,
  PERMISSIONS.SYSTEM_BACKFILL,
  // #1964: both end in `:read`, so the viewer preset would otherwise derive
  // them — handing the raw-payload order trace to every viewer.
  PERMISSIONS.AUDIT_READ,
  PERMISSIONS.DEBUG_READ,
];

// Mirrors backend `ROLE_DEFAULT_PERMISSIONS`: admin gets the full
// catalog, operator the daily-operations surface (catalog minus the
// excluded three), viewer the read-only face (every `:read` except
// `users:read`, so a viewer never outranks an operator on identity).
// Derived from the catalog on both sides so a new key lands in the
// right presets without a second edit — an empty preset here used to
// mean an invited operator held nothing and saw a fully disabled
// sidebar. RoleEditor reads these as the "is this seat customized?"
// baseline and as the fallback when the grants query fails, so drift
// from the backend constant shows admins a diff the server never applied.
export const ROLE_PRESETS: Record<string, string[]> = {
  admin: [...ALL_PERMISSIONS],
  operator: ALL_PERMISSIONS.filter((p) => !NON_ADMIN_EXCLUDED.includes(p)),
  viewer: ALL_PERMISSIONS.filter((p) => p.endsWith(':read') && !NON_ADMIN_EXCLUDED.includes(p)),
};
